/**
 * Вкладка «Справочник ФККО» раздела «Экология» панели администратора (ecology-fkko). Справочник
 * подставляет наименование отхода по коду ФККО и код по наименованию в поле «Отходы по ФККО»
 * карточки строки (паспорта отходов). Заказчик пришлёт файл справочника — администратор загружает
 * его целиком: прежний справочник заменяется, отходы, которые клиенты уже внесли, не меняются.
 * Правки отдельных записей нет — официальный классификатор меняется приказом, а не вручную
 * (решение Р13 плана). Пока справочник пуст, клиенты вводят код и наименование сами.
 *
 * Поиск на вкладке — тот же, что у подсказок клиента: так после загрузки видно, что справочник
 * разобрался правильно.
 */
import { useEffect, useRef, useState } from 'react'
import { Upload } from 'lucide-react'
import { adminApi, ecologyApi, type EcoFkkoEntry, type EcoFkkoStats } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { fkkoHazardClass, formatFkkoCode } from '@/lib/ecology'
import { pluralRu } from '@/lib/orders'
import { Panel } from '@/design-system/components/layout/panel'
import { Toolbar } from '@/design-system/components/layout/toolbar'
import { Button } from '@/design-system/components/primitives/button'
import { Input } from '@/design-system/components/primitives/input'
import { ConfirmDialog } from '@/design-system/components/primitives/confirm-dialog'
import { Alert } from '@/design-system/components/feedback/alert'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
} from '@/design-system/components/data/data-table'

/** Файлы, которые принимает загрузка справочника (как `imports.ParseFKKO`). */
const FKKO_ACCEPT = '.xlsx,.xls,.csv,.docx'

/** «18.09.2026» из ISO-времени загрузки. */
function dateLabel(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('ru-RU')
}

const records = (n: number) => `${n} ${pluralRu(n, 'запись', 'записи', 'записей')}`

export function FkkoTab() {
  const [stats, setStats] = useState<EcoFkkoStats | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [pending, setPending] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [result, setResult] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [found, setFound] = useState<EcoFkkoEntry[] | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const controller = new AbortController()
    adminApi.ecology
      .fkkoStats(controller.signal)
      .then((s) => {
        if (!controller.signal.aborted) setStats(s)
      })
      .catch((err) => {
        if (!controller.signal.aborted)
          setLoadError(getErrorMessage(err, 'Не удалось загрузить состояние справочника'))
      })
    return () => controller.abort()
  }, [])

  // Поиск по справочнику — с задержкой, как подсказки у клиента; пересчитывается и после загрузки.
  useEffect(() => {
    const q = query.trim()
    if (!q) {
      setFound(null)
      return
    }
    const controller = new AbortController()
    const handle = setTimeout(() => {
      ecologyApi
        .searchFkko(q, controller.signal)
        .then((res) => {
          if (!controller.signal.aborted) setFound(res.items)
        })
        .catch(() => {
          if (!controller.signal.aborted) setFound([])
        })
    }, 250)
    return () => {
      clearTimeout(handle)
      controller.abort()
    }
  }, [query, stats])

  async function upload(file: File) {
    setPending(null)
    setError(null)
    setResult(null)
    setUploading(true)
    try {
      const res = await adminApi.ecology.importFkko(file)
      setStats(res)
      setResult(
        `Загружено видов отходов: ${res.total}.` +
          (res.groups > 0 ? ` Группы каталога пропущены: ${res.groups}.` : '') +
          (res.skipped > 0
            ? ` Не вошло строк вида отхода: ${res.skipped} — повтор кода или нет наименования.`
            : ''),
      )
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось загрузить справочник'))
    } finally {
      setUploading(false)
    }
  }

  const filled = stats !== null && stats.total > 0

  return (
    <div>
      <Toolbar aria-label="Загрузка справочника ФККО">
        <p className="min-w-56 flex-1 text-14 text-txt">
          {loadError ? null : stats === null ? (
            'Загрузка…'
          ) : filled ? (
            <>
              В справочнике {records(stats.total)}
              {stats.importedAt ? `, загружен ${dateLabel(stats.importedAt)}` : ''}
            </>
          ) : (
            'Справочник пуст — клиенты вводят код и наименование отхода вручную'
          )}
        </p>
        <Button
          disabled={uploading || stats === null}
          onClick={() => fileRef.current?.click()}
          leftIcon={<Upload className="size-4 stroke-current" aria-hidden />}
        >
          {uploading ? 'Загрузка…' : filled ? 'Заменить из файла' : 'Загрузить из файла'}
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept={FKKO_ACCEPT}
          className="hidden"
          aria-label="Файл справочника ФККО"
          onChange={(e) => {
            const file = e.target.files?.[0]
            e.target.value = ''
            if (!file) return
            // Замена непустого справочника — после подтверждения.
            if (filled) setPending(file)
            else void upload(file)
          }}
        />
      </Toolbar>
      <p className="mb-3 text-12 text-txt-tertiary">
        Excel (.xlsx, .xls), CSV или Word с таблицей: в строке — код ФККО (11 цифр, можно с
        пробелами между разрядами) и следом наименование отхода; шапка и строки без кода
        пропускаются. В справочник идут только виды отходов — группы каталога (код оканчивается
        нулём) пропускаются. Справочник заменяется целиком; отходы, которые клиенты уже внесли, не
        меняются.
      </p>

      {loadError ? (
        <Alert variant="danger" className="mb-4">
          {loadError}
        </Alert>
      ) : null}
      {error ? (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      ) : null}
      {result ? (
        <Alert variant="info" className="mb-4">
          {result}
        </Alert>
      ) : null}

      {filled ? (
        <Panel>
          <div className="p-4">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Проверить: код или слова наименования"
              aria-label="Поиск по справочнику ФККО"
            />
          </div>
          {found === null ? null : found.length === 0 ? (
            <p className="px-4 pb-4 text-13 text-txt-secondary">Ничего не найдено</p>
          ) : (
            <div className="overflow-x-auto">
              <DataTable>
                <DataTableHead>
                  <tr>
                    <DataTableHeaderCell>Код ФККО</DataTableHeaderCell>
                    <DataTableHeaderCell>Наименование отхода</DataTableHeaderCell>
                    <DataTableHeaderCell>Класс опасности</DataTableHeaderCell>
                  </tr>
                </DataTableHead>
                <DataTableBody>
                  {found.map((e) => (
                    <DataTableRow key={e.code}>
                      <DataTableCell className="whitespace-nowrap tabular-nums">
                        {formatFkkoCode(e.code)}
                      </DataTableCell>
                      <DataTableCell>{e.name}</DataTableCell>
                      <DataTableCell className="whitespace-nowrap text-txt-secondary">
                        {fkkoHazardClass(e.code)}
                      </DataTableCell>
                    </DataTableRow>
                  ))}
                </DataTableBody>
              </DataTable>
            </div>
          )}
        </Panel>
      ) : null}

      <ConfirmDialog
        open={pending !== null}
        title="Замена справочника ФККО"
        message={
          pending && stats
            ? `Заменить справочник (${records(stats.total)}) содержимым файла «${pending.name}»? Отходы, которые клиенты уже внесли, не изменятся.`
            : ''
        }
        onClose={() => setPending(null)}
        buttons={[
          {
            label: 'Заменить',
            variant: 'danger',
            onClick: () => {
              if (pending) void upload(pending)
            },
          },
          { label: 'Отмена', variant: 'secondary', onClick: () => setPending(null) },
        ]}
      />
    </div>
  )
}
